import { useEffect, useState } from "react";
import { SEO } from "../components/SEO";
import { PageHero } from "../components/PageHero";
import { Section, SectionHeader } from "../components/Section";
import { Card } from "../components/Card";
import { CTASection } from "../components/CTASection";
import { getOnlinePrograms, TRAINING_CATALOG, type OnlineProgram } from "../lib/wix/programs";
import { useTranslation } from "../lib/i18n";

const MODULE_TYPE_KEY: Record<string, string> = {
  lesson: "training.module.lesson",
  quiz: "training.module.quiz",
  assignment: "training.module.assignment",
  simulation: "training.module.simulation",
};

export default function TrainingPage() {
  const { t } = useTranslation();
  const [programs, setPrograms] = useState<OnlineProgram[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getOnlinePrograms().then((p) => {
      setPrograms(p.length > 0 ? p : TRAINING_CATALOG);
      setLoading(false);
    });
  }, []);

  return (
    <>
      <SEO
        title="Training — Sentinel"
        description="Sentinel training powered by Wix Online Programs. Core modules, advanced modules, quizzes, assignments, simulations, and domain templates."
        canonical="/training"
      />
      <PageHero
        eyebrow={t("training.page.eyebrow")}
        title={t("training.page.title")}
        description={t("training.page.description")}
        bgVariant="amber"
        bgImage="https://images.pexels.com/photos/18999556/pexels-photo-18999556.jpeg?auto=compress&cs=tinysrgb&w=1600"
      />

      <Section>
        <SectionHeader
          eyebrow={t("training.catalog.eyebrow")}
          title={t("training.catalog.title")}
          description={t("training.catalog.description")}
        />
        {loading ? (
          <p className="text-center text-muted">{t("common.loadingPrograms")}</p>
        ) : (
          <div className="grid grid-2">
            {programs.map((program) => (
              <Card key={program.id} hover>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1rem" }}>
                  <div>
                    <span
                      className="badge"
                      style={{
                        background: program.level === "core" ? "var(--primary-50)" : "var(--accent-50)",
                        color: program.level === "core" ? "var(--primary-700)" : "var(--accent-700)",
                      }}
                    >
                      {program.level === "core" ? t("common.core") : t("common.advanced")}
                    </span>
                    <h3 style={{ fontSize: "1.125rem", marginTop: "0.75rem", marginBottom: "0.25rem" }}>
                      {program.name}
                    </h3>
                  </div>
                  <span className="text-muted" style={{ fontSize: "0.8125rem", whiteSpace: "nowrap" }}>
                    {program.duration}
                  </span>
                </div>
                <p className="text-muted" style={{ fontSize: "0.9375rem", marginBottom: "1rem" }}>
                  {program.description}
                </p>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                  {program.modules.map((m) => (
                    <div
                      key={m.id}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "0.5rem 0.75rem",
                        borderRadius: "var(--radius-md)",
                        background: "var(--neutral-50)",
                      }}
                    >
                      <span style={{ fontSize: "0.875rem" }}>{m.title}</span>
                      <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
                        <span className="badge" style={{ background: "var(--neutral-100)", color: "var(--neutral-600)", fontSize: "0.75rem" }}>
                          {MODULE_TYPE_KEY[m.type] ? t(MODULE_TYPE_KEY[m.type]) : m.type}
                        </span>
                        <span className="text-muted" style={{ fontSize: "0.75rem" }}>{m.duration}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        )}
      </Section>

      <CTASection
        title={t("training.cta.title")}
        description={t("training.cta.description")}
        primaryLabel={t("cta.registerNow")}
        primaryTo="/register"
        secondaryLabel={t("cta.viewCertification")}
        secondaryTo="/certification"
      />
    </>
  );
}
